import { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, Search, FileText, MapPin, UserCheck, Users, ArrowRight, ClipboardList, Pencil, Trash2, Briefcase, RefreshCw, Zap, Calendar, Clock, ShieldCheck, AlertTriangle } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { Switch } from '../components/ui/switch';

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [isTimelineDialogOpen, setIsTimelineDialogOpen] = useState(false);
  const [timelineClient, setTimelineClient] = useState(null);
  const [timelineActivities, setTimelineActivities] = useState([]);
  const [timelineOverrides, setTimelineOverrides] = useState({});
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedAdminId, setSelectedAdminId] = useState('');
  const [transferTasks, setTransferTasks] = useState(false);
  const [newAssigneeId, setNewAssigneeId] = useState('default-admin');
  const [clientTaskCount, setClientTaskCount] = useState(0);
  // Per-service admin assignment (one client → multiple admins for different services)
  const [isServiceAssignOpen, setIsServiceAssignOpen] = useState(false);
  const [serviceAssignClient, setServiceAssignClient] = useState(null);
  const [serviceAdminMap, setServiceAdminMap] = useState({}); // { serviceName: adminId | '' }
  const [savingServiceMap, setSavingServiceMap] = useState(false);
  // Duplicate finder
  const [isDupOpen, setIsDupOpen] = useState(false);
  const [dupLoading, setDupLoading] = useState(false);
  const [dupData, setDupData] = useState({ duplicate_company_groups: [], duplicate_gst_groups: [] });

  const fetchDuplicates = async () => {
    setDupLoading(true);
    try {
      const res = await api.get('/admin/duplicate-clients');
      setDupData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load duplicates');
    } finally {
      setDupLoading(false);
    }
  };

  const deleteDuplicateClient = async (clientId) => {
    if (!window.confirm('Permanently delete this client? Their tasks will also be deleted. This cannot be undone.')) return;
    try {
      await api.delete(`/clients/${clientId}`);
      toast.success('Client deleted');
      await fetchDuplicates();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete client');
    }
  };
  const [formData, setFormData] = useState({
    company: '',
    gst_number: '',
    is_gst_registered: true,
    branch: '',
    client_type: 'one_time',
    services_provided: []
  });
  const [editFormData, setEditFormData] = useState({
    company: '',
    gst_number: '',
    is_gst_registered: true,
    branch: '',
    client_type: 'one_time',
    services_provided: []
  });
  
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = currentUser.role === 'super_admin';
  const canAssign = currentUser.role === 'super_admin' || currentUser.role === 'admin';
  const canEditDelete = currentUser.role === 'super_admin' || currentUser.role === 'admin';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [clientsRes, usersRes, servicesRes] = await Promise.all([
        api.get('/clients'),
        api.get('/users'),
        api.get('/services').catch(() => ({ data: [] }))
      ]);
      setClients(clientsRes.data);
      setAllUsers(usersRes.data);
      setServices(servicesRes.data);
      // Filter to get only admins for assignment dropdown
      // Include both Admins and Super Admins in the assignable-admin list
      // (Jun 2026 — Super Admins now act as case owners on certain clients).
      setAdmins(usersRes.data.filter(u => u.role === 'admin' || u.role === 'super_admin'));
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    
    // Validate mandatory fields
    if (!formData.company.trim() || !formData.branch.trim()) {
      toast.error('Company name and branch are mandatory');
      return;
    }
    if (formData.is_gst_registered && !formData.gst_number.trim()) {
      toast.error('GST number is required for GST-registered clients');
      return;
    }
    
    try {
      const payload = { ...formData };
      if (!payload.is_gst_registered) payload.gst_number = null;
      await api.post('/clients', payload);
      toast.success('Client created successfully');
      setIsCreateDialogOpen(false);
      setFormData({ company: '', gst_number: '', is_gst_registered: true, branch: '', client_type: 'one_time', services_provided: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create client');
    }
  };

  const handleEditClient = (client) => {
    setSelectedClient(client);
    setEditFormData({
      company: client.company,
      gst_number: client.gst_number || '',
      is_gst_registered: client.is_gst_registered !== false,
      branch: client.branch,
      client_type: client.client_type || 'one_time',
      services_provided: client.services_provided || []
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdateClient = async (e) => {
    e.preventDefault();
    
    // Validate mandatory fields
    if (!editFormData.company.trim() || !editFormData.branch.trim()) {
      toast.error('Company name and branch are mandatory');
      return;
    }
    if (editFormData.is_gst_registered && !editFormData.gst_number.trim()) {
      toast.error('GST number is required for GST-registered clients');
      return;
    }
    
    try {
      const payload = { ...editFormData };
      if (!payload.is_gst_registered) payload.gst_number = null;
      await api.put(`/clients/${selectedClient.id}`, payload);
      toast.success('Client updated successfully');
      setIsEditDialogOpen(false);
      setSelectedClient(null);
      setEditFormData({ company: '', gst_number: '', is_gst_registered: true, branch: '', client_type: 'one_time', services_provided: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update client');
    }
  };

  const handleDeleteClick = (client) => {
    setSelectedClient(client);
    setIsDeleteDialogOpen(true);
  };

  const handleGenerateTasks = async (clientId) => {
    try {
      const res = await api.post(`/clients/${clientId}/generate-tasks`);
      if (res.data.created > 0) {
        toast.success(res.data.message);
      } else {
        toast.info('All tasks already exist for this client');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate tasks');
    }
  };

  // Timeline overrides
  const openTimelineDialog = async (client) => {
    setTimelineClient(client);
    setIsTimelineDialogOpen(true);
    setTimelineLoading(true);
    try {
      // Fetch all activities for client's services
      const clientServices = client.services_provided || [];
      const allActivities = [];
      for (const svc of services) {
        if (clientServices.includes(svc.name)) {
          const res = await api.get(`/services/${svc.id}/activities`);
          allActivities.push(...res.data.map(a => ({ ...a, service_name: svc.name })));
        }
      }
      setTimelineActivities(allActivities);
      // Fetch existing overrides
      const overridesRes = await api.get(`/clients/${client.id}/timeline-overrides`);
      const overrideMap = {};
      overridesRes.data.forEach(o => { overrideMap[o.activity_id] = o; });
      setTimelineOverrides(overrideMap);
    } catch { toast.error('Failed to load timelines'); }
    finally { setTimelineLoading(false); }
  };

  const handleOverrideChange = (activityId, field, value) => {
    setTimelineOverrides(prev => ({
      ...prev,
      [activityId]: { ...(prev[activityId] || {}), activity_id: activityId, [field]: value }
    }));
  };

  const saveOverride = async (activityId) => {
    const override = timelineOverrides[activityId];
    if (!override) return;
    try {
      await api.put(`/clients/${timelineClient.id}/timeline-overrides/${activityId}`, {
        custom_deadline_day: override.custom_deadline_day ? parseInt(override.custom_deadline_day) : null,
      });
      toast.success('Override saved');
    } catch { toast.error('Failed to save override'); }
  };

  const handleDeleteClient = async () => {
    try {
      await api.delete(`/clients/${selectedClient.id}`);
      toast.success('Client deleted successfully');
      setIsDeleteDialogOpen(false);
      setSelectedClient(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete client');
    }
  };

  const handleAssignClient = async () => {
    if (!selectedClient || !selectedAdminId) return;
    
    const isUnassign = selectedAdminId === 'unassign';
    
    try {
      const payload = { 
        admin_id: isUnassign ? null : selectedAdminId,
        transfer_tasks: transferTasks && !isUnassign,
        new_assignee_id: (newAssigneeId && newAssigneeId !== 'default-admin') ? newAssigneeId : null
      };
      
      const response = await api.put(`/clients/${selectedClient.id}/assign`, payload);
      
      let message = isUnassign ? 'Client unassigned' : 'Client assigned successfully';
      if (response.data.tasks_transferred > 0) {
        message += `. ${response.data.tasks_transferred} task(s) transferred.`;
      }
      
      toast.success(message);
      closeAssignDialog();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign client');
    }
  };

  const openAssignDialog = async (client) => {
    setSelectedClient(client);
    setSelectedAdminId('');
    setTransferTasks(false);
    setNewAssigneeId('');
    
    // Get task count for this client
    try {
      const tasksRes = await api.get('/tasks');
      const clientTasks = tasksRes.data.filter(t => t.client_id === client.id);
      setClientTaskCount(clientTasks.length);
    } catch {
      setClientTaskCount(0);
    }
    
    setIsAssignDialogOpen(true);
  };

  const closeAssignDialog = () => {
    setIsAssignDialogOpen(false);
    setSelectedClient(null);
    setSelectedAdminId('');
    setTransferTasks(false);
    setNewAssigneeId('');
    setClientTaskCount(0);
  };

  const openServiceAssignDialog = (client) => {
    setServiceAssignClient(client);
    const map = {};
    (client.services_provided || []).forEach(svc => {
      map[svc] = (client.service_admin_map || {})[svc] || '';
    });
    setServiceAdminMap(map);
    setIsServiceAssignOpen(true);
  };

  const closeServiceAssignDialog = () => {
    setIsServiceAssignOpen(false);
    setServiceAssignClient(null);
    setServiceAdminMap({});
  };

  const saveServiceAssignments = async () => {
    if (!serviceAssignClient) return;
    setSavingServiceMap(true);
    try {
      // Drop empty entries before sending
      const cleaned = Object.fromEntries(
        Object.entries(serviceAdminMap).filter(([, v]) => v && v !== 'none')
      );
      await api.put(`/clients/${serviceAssignClient.id}/service-assignments`, {
        service_admin_map: cleaned
      });
      toast.success('Service assignments updated. New tasks generated will follow this mapping.');
      closeServiceAssignDialog();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save service assignments');
    } finally {
      setSavingServiceMap(false);
    }
  };

  // Returns list of unique admin IDs assigned to a client (client-level + service-level)
  const getClientAdminIds = (client) => {
    const ids = new Set();
    if (client.assigned_admin_id) ids.add(client.assigned_admin_id);
    Object.values(client.service_admin_map || {}).forEach(id => { if (id) ids.add(id); });
    return Array.from(ids);
  };

  const getAssignedAdminName = (adminId) => {
    const admin = allUsers.find(a => a.id === adminId);
    return admin ? admin.full_name : 'Unassigned';
  };

  // Check if this is a reassignment (client has existing admin and we're assigning to different one)
  const isReassignment = selectedClient?.assigned_admin_id && 
    selectedAdminId && 
    selectedAdminId !== 'unassign' && 
    selectedAdminId !== selectedClient.assigned_admin_id;

  // Filter + sort: alphabetical by company (case-insensitive, locale-aware so
  // "Z" sorts after "A" and accented characters slot in correctly). Trim
  // before comparing so any leading/trailing whitespace in DB rows doesn't
  // bubble them to the top — `' AADVIK'` would otherwise outsort 'Adyar'.
  const filteredClients = clients
    .filter(client =>
      client.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (client.gst_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.branch.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => (a.company || '').trim().localeCompare((b.company || '').trim(), undefined, { sensitivity: 'base' }));

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="p-6 h-full flex flex-col overflow-hidden" style={{
        background: 'linear-gradient(135deg, rgba(91, 111, 214, 0.03) 0%, rgba(16, 185, 129, 0.03) 100%)'
      }}>
        <div className="mb-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h1 className="text-2xl font-bold tracking-tight mb-1" style={{
              background: 'linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>Clients</h1>
            <p className="text-sm text-slate-600">
              {isSuperAdmin 
                ? 'Manage all clients and assign them to admins' 
                : canEditDelete
                  ? 'View and manage your assigned clients'
                  : 'View clients assigned to you'}
            </p>
          </div>
          {canEditDelete && (
            <div className="flex items-center gap-2">
              {isSuperAdmin && (
                <Button variant="outline" onClick={() => { setIsDupOpen(true); fetchDuplicates(); }}
                  className="text-amber-700 border-amber-300 hover:bg-amber-50"
                  data-testid="find-duplicates-button">
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Find Duplicates
                </Button>
              )}
              <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-gradient-to-r from-[#5B6FD6] to-[#10B981] hover:from-[#4a5bc5] hover:to-[#0d9668] shadow-md" data-testid="create-client-button">
                    <Plus className="h-5 w-5 mr-2" />
                    Add Client
                  </Button>
                </DialogTrigger>
                <DialogContent data-testid="create-client-dialog">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-bold">Add New Client</DialogTitle>
                    <DialogDescription className="sr-only">Add a new client to the system</DialogDescription>
                  </DialogHeader>
                <form onSubmit={handleCreateClient} className="space-y-4">
                  <div>
                    <Label htmlFor="company">Company Name <span className="text-red-600">*</span></Label>
                    <Input
                      id="company"
                      data-testid="client-company-input"
                      placeholder="ABC Corp Pvt Ltd"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      required
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-slate-50">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className={`h-4 w-4 ${formData.is_gst_registered ? 'text-emerald-600' : 'text-slate-400'}`} />
                      <Label htmlFor="gst-toggle" className="text-sm font-medium cursor-pointer">GST Registered</Label>
                    </div>
                    <Switch
                      id="gst-toggle"
                      checked={formData.is_gst_registered}
                      onCheckedChange={(v) => setFormData({ ...formData, is_gst_registered: v, gst_number: v ? formData.gst_number : '' })}
                      data-testid="client-gst-toggle"
                    />
                  </div>

                  {formData.is_gst_registered && (
                    <div>
                      <Label htmlFor="gst_number">GST Number <span className="text-red-600">*</span></Label>
                      <Input
                        id="gst_number"
                        data-testid="client-gst-input"
                        placeholder="22AAAAA0000A1Z5"
                        value={formData.gst_number}
                        onChange={(e) => setFormData({ ...formData, gst_number: e.target.value })}
                        required
                      />
                    </div>
                  )}

                  <div>
                    <Label htmlFor="branch">Branch <span className="text-red-600">*</span></Label>
                    <Input
                      id="branch"
                      data-testid="client-branch-input"
                      placeholder="Mumbai Head Office"
                      value={formData.branch}
                      onChange={(e) => setFormData({ ...formData, branch: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="client_type">Client Type <span className="text-red-600">*</span></Label>
                    <Select
                      value={formData.client_type}
                      onValueChange={(value) => setFormData({ ...formData, client_type: value })}
                    >
                      <SelectTrigger data-testid="client-type-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="retainer">
                          <div className="flex items-center gap-2">
                            <RefreshCw className="h-3 w-3" />
                            Retainer
                          </div>
                        </SelectItem>
                        <SelectItem value="one_time">
                          <div className="flex items-center gap-2">
                            <Briefcase className="h-3 w-3" />
                            One-Time Service
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Services as per Agreement</Label>
                    <div className="mt-2 grid grid-cols-2 gap-2 max-h-36 overflow-y-auto p-2 border rounded-md bg-slate-50">
                      {services.length === 0 ? (
                        <p className="text-xs text-slate-500 col-span-2">No services available. Add services in Services tab first.</p>
                      ) : (
                        services.map(service => (
                          <label key={service.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-white p-1 rounded">
                            <Checkbox
                              checked={formData.services_provided.includes(service.name)}
                              onCheckedChange={(checked) => {
                                setFormData(prev => ({
                                  ...prev,
                                  services_provided: checked
                                    ? [...prev.services_provided, service.name]
                                    : prev.services_provided.filter(s => s !== service.name)
                                }));
                              }}
                              data-testid={`create-service-checkbox-${service.id}`}
                            />
                            <span>{service.name}</span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      className="bg-gradient-to-r from-[#5B6FD6] to-[#10B981]" 
                      data-testid="client-submit-button"
                      disabled={!formData.company.trim() || !formData.branch.trim() || (formData.is_gst_registered && !formData.gst_number.trim())}
                    >
                      Add Client
                    </Button>
                  </div>
                </form>
              </DialogContent>
              </Dialog>
            </div>
          )}
        </div>

        {/* Search */}
        <div className="mb-3 flex-shrink-0">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search clients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-9 bg-white/80"
              data-testid="search-clients-input"
            />
          </div>
        </div>

        {/* Clients List View */}
        <Card className="border-slate-200 bg-white/80 backdrop-blur-sm flex-1 min-h-0 overflow-hidden" data-testid="clients-list">
          <CardContent className="p-0 h-full flex flex-col">
            {filteredClients.length === 0 ? (
              <div className="p-12 text-center">
                <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 text-lg">No clients found</p>
              </div>
            ) : (
              <div className="overflow-auto flex-1">
                <table className="w-full">
                  <thead className="sticky top-0 z-10 bg-slate-50">
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 px-4 text-xs font-semibold text-slate-700 uppercase tracking-wider">Company</th>
                      <th className="text-left py-2 px-4 text-xs font-semibold text-slate-700 uppercase tracking-wider">GST Number</th>
                      <th className="text-left py-2 px-4 text-xs font-semibold text-slate-700 uppercase tracking-wider">Branch</th>
                      <th className="text-left py-2 px-4 text-xs font-semibold text-slate-700 uppercase tracking-wider">Type</th>
                      {canAssign && (
                        <th className="text-left py-2 px-4 text-xs font-semibold text-slate-700 uppercase tracking-wider">Assigned To</th>
                      )}
                      <th className="text-right py-2 px-4 text-xs font-semibold text-slate-700 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredClients.map((client, index) => (
                      <tr 
                        key={client.id} 
                        className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-slate-25'}`}
                        data-testid={`client-row-${client.id}`}
                      >
                        <td className="py-1.5 px-4">
                          <div className="flex items-center gap-2">
                            <div className="h-7 w-7 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0 shadow-sm" style={{
                              background: client.assigned_admin_id 
                                ? 'linear-gradient(135deg, #5B6FD6 0%, #10B981 100%)'
                                : 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)'
                            }}>
                              {client.company.charAt(0).toUpperCase()}
                            </div>
                            <span className="font-medium text-sm text-slate-900">{client.company}</span>
                          </div>
                        </td>
                        <td className="py-1.5 px-4">
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <FileText className={`h-3.5 w-3.5 ${client.is_gst_registered !== false ? 'text-[#5B6FD6]' : 'text-slate-300'}`} />
                            {client.gst_number ? (
                              <span>{client.gst_number}</span>
                            ) : (
                              <Badge variant="outline" className="text-[10px] text-slate-400 border-slate-200">Non-GST</Badge>
                            )}
                          </div>
                        </td>
                        <td className="py-1.5 px-4">
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <MapPin className="h-3.5 w-3.5 text-[#10B981]" />
                            <span>{client.branch}</span>
                          </div>
                        </td>
                        <td className="py-1.5 px-4">
                          <Badge className={client.client_type === 'retainer' 
                            ? 'bg-[#5B6FD6]/10 text-[#5B6FD6] text-xs' 
                            : 'bg-slate-100 text-slate-600 text-xs'
                          } data-testid={`client-type-${client.id}`}>
                            {client.client_type === 'retainer' ? 'Retainer' : 'One-Time'}
                          </Badge>
                        </td>
                        {canAssign && (
                          <td className="py-1.5 px-4">
                            <div className="flex flex-wrap items-center gap-1">
                              {(() => {
                                const adminIds = getClientAdminIds(client);
                                if (adminIds.length === 0) {
                                  return (
                                    <Badge
                                      className="cursor-pointer text-xs bg-slate-100 text-slate-500 hover:bg-slate-200"
                                      onClick={() => openAssignDialog(client)}
                                    >
                                      <UserCheck className="h-3 w-3 mr-1" />
                                      Unassigned
                                    </Badge>
                                  );
                                }
                                return adminIds.map(aid => (
                                  <Badge
                                    key={aid}
                                    className="cursor-pointer text-xs bg-[#10B981]/10 text-[#10B981] hover:bg-[#10B981]/20"
                                    onClick={() => openAssignDialog(client)}
                                    data-testid={`client-admin-badge-${client.id}-${aid}`}
                                  >
                                    <UserCheck className="h-3 w-3 mr-1" />
                                    {getAssignedAdminName(aid)}
                                  </Badge>
                                ));
                              })()}
                            </div>
                          </td>
                        )}
                        <td className="py-1.5 px-4">
                          <div className="flex items-center justify-end gap-1.5">
                            {canEditDelete && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => openTimelineDialog(client)}
                                className="h-7 px-2 text-xs text-amber-600 border-amber-300 hover:bg-amber-50"
                                data-testid={`timeline-overrides-${client.id}`}
                              >
                                <Clock className="h-3 w-3 mr-1" />
                                Timelines
                              </Button>
                            )}
                            {canEditDelete && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleGenerateTasks(client.id)}
                                className="h-7 px-2 text-xs text-[#10B981] border-[#10B981]/30 hover:bg-[#10B981]/10"
                                data-testid={`generate-tasks-${client.id}`}
                              >
                                <Zap className="h-3 w-3 mr-1" />
                                Generate
                              </Button>
                            )}
                            {canAssign && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => openAssignDialog(client)}
                                className="h-7 px-2 text-xs text-[#5B6FD6] border-[#5B6FD6]/30 hover:bg-[#5B6FD6]/10"
                                data-testid={`assign-client-${client.id}`}
                              >
                                <UserCheck className="h-3 w-3 mr-1" />
                                Assign
                              </Button>
                            )}
                            {canAssign && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openServiceAssignDialog(client)}
                                className="h-7 px-2 text-xs text-purple-600 border-purple-300 hover:bg-purple-50"
                                data-testid={`service-assign-${client.id}`}
                                title="Assign different services to different teams"
                              >
                                <Briefcase className="h-3 w-3 mr-1" />
                                Per-Service
                              </Button>
                            )}
                            {canEditDelete && (
                              <>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => handleEditClient(client)}
                                  className="h-7 w-7 p-0 text-slate-600 hover:text-[#5B6FD6] hover:border-[#5B6FD6]/30"
                                  data-testid={`edit-client-${client.id}`}
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => handleDeleteClick(client)}
                                  className="h-7 w-7 p-0 text-slate-600 hover:text-red-600 hover:border-red-300"
                                  data-testid={`delete-client-${client.id}`}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Assign Client Dialog */}
        <Dialog open={isAssignDialogOpen} onOpenChange={closeAssignDialog}>
          <DialogContent className="max-w-md" data-testid="assign-client-dialog">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold flex items-center gap-2">
                <UserCheck className="h-5 w-5 text-[#5B6FD6]" />
                {selectedClient?.assigned_admin_id ? 'Reassign Client' : 'Assign Client'}
              </DialogTitle>
              <DialogDescription>
                {selectedClient?.assigned_admin_id 
                  ? `Transfer "${selectedClient?.company}" from ${getAssignedAdminName(selectedClient?.assigned_admin_id)} to another admin.`
                  : `Assign "${selectedClient?.company}" to an admin who will manage this client's tasks.`
                }
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-5 pt-2">
              {/* Current Assignment Info */}
              {selectedClient?.assigned_admin_id && (
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="text-sm text-slate-600 mb-1">Currently assigned to:</div>
                  <div className="font-medium text-slate-900">
                    {getAssignedAdminName(selectedClient?.assigned_admin_id)}
                  </div>
                  {clientTaskCount > 0 && (
                    <div className="flex items-center gap-1 mt-2 text-sm text-amber-600">
                      <ClipboardList className="h-4 w-4" />
                      {clientTaskCount} active task{clientTaskCount > 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              )}

              {/* Select New Admin */}
              <div>
                <Label className="text-sm font-medium">New Admin</Label>
                <Select value={selectedAdminId} onValueChange={setSelectedAdminId}>
                  <SelectTrigger data-testid="assign-admin-select" className="mt-1.5">
                    <SelectValue placeholder="Choose an admin..." />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedClient?.assigned_admin_id && (
                      <SelectItem value="unassign">
                        <span className="text-red-600">Remove Assignment</span>
                      </SelectItem>
                    )}
                    {admins
                      .filter(a => a.id && a.id !== selectedClient?.assigned_admin_id)
                      .map(admin => (
                        <SelectItem key={admin.id} value={admin.id}>
                          {admin.full_name}{admin.role === 'super_admin' ? ' (Super Admin)' : ''}
                        </SelectItem>
                      ))
                    }
                    {admins.filter(a => a.id !== selectedClient?.assigned_admin_id).length === 0 && (
                      <SelectItem value="no-admins" disabled>
                        No other admins available
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Task Transfer Option - Only show when reassigning and there are tasks */}
              {isReassignment && clientTaskCount > 0 && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="transfer_tasks"
                      checked={transferTasks}
                      onCheckedChange={setTransferTasks}
                      data-testid="transfer-tasks-checkbox"
                    />
                    <div className="flex-1">
                      <Label 
                        htmlFor="transfer_tasks" 
                        className="text-sm font-medium cursor-pointer"
                      >
                        Transfer {clientTaskCount} task{clientTaskCount > 1 ? 's' : ''} to new admin
                      </Label>
                      <p className="text-xs text-slate-600 mt-1">
                        All tasks for this client will be reassigned to the new admin's team
                      </p>
                    </div>
                  </div>

                  {/* Select specific assignee if transferring tasks */}
                  {transferTasks && (
                    <div className="mt-4 pt-4 border-t border-blue-200">
                      <Label className="text-sm font-medium">Assign tasks to</Label>
                      <Select value={newAssigneeId} onValueChange={setNewAssigneeId}>
                        <SelectTrigger data-testid="new-assignee-select" className="mt-1.5">
                          <SelectValue placeholder="New admin (default)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default-admin">
                            {admins.find(a => a.id === selectedAdminId)?.full_name || 'New Admin'} (default)
                          </SelectItem>
                          {allUsers
                            .filter(u => u.id && u.role === 'member')
                            .map(user => (
                              <SelectItem key={user.id} value={user.id}>
                                {user.full_name} (Member)
                              </SelectItem>
                            ))
                          }
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}

              {/* Visual Transfer Indicator */}
              {isReassignment && transferTasks && (
                <div className="flex items-center justify-center gap-3 py-2 text-sm text-slate-600">
                  <span className="font-medium">{getAssignedAdminName(selectedClient?.assigned_admin_id)}</span>
                  <ArrowRight className="h-4 w-4 text-[#5B6FD6]" />
                  <span className="font-medium text-[#10B981]">
                    {admins.find(a => a.id === selectedAdminId)?.full_name || 'New Admin'}
                  </span>
                </div>
              )}
            </div>

            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={closeAssignDialog}>
                Cancel
              </Button>
              <Button 
                onClick={handleAssignClient}
                disabled={!selectedAdminId}
                className="bg-gradient-to-r from-[#5B6FD6] to-[#10B981]"
                data-testid="confirm-assign-button"
              >
                {selectedAdminId === 'unassign' ? 'Remove Assignment' : (
                  transferTasks ? `Assign & Transfer ${clientTaskCount} Tasks` : 'Assign Client'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Client Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent data-testid="edit-client-dialog">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">Edit Client</DialogTitle>
              <DialogDescription className="sr-only">Edit client details</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateClient} className="space-y-4">
              <div>
                <Label htmlFor="edit-company">Company Name <span className="text-red-600">*</span></Label>
                <Input
                  id="edit-company"
                  data-testid="edit-client-company-input"
                  placeholder="ABC Corp Pvt Ltd"
                  value={editFormData.company}
                  onChange={(e) => setEditFormData({ ...editFormData, company: e.target.value })}
                  required
                />
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-slate-50">
                <div className="flex items-center gap-2">
                  <ShieldCheck className={`h-4 w-4 ${editFormData.is_gst_registered ? 'text-emerald-600' : 'text-slate-400'}`} />
                  <Label htmlFor="edit-gst-toggle" className="text-sm font-medium cursor-pointer">GST Registered</Label>
                </div>
                <Switch
                  id="edit-gst-toggle"
                  checked={editFormData.is_gst_registered}
                  onCheckedChange={(v) => setEditFormData({ ...editFormData, is_gst_registered: v, gst_number: v ? editFormData.gst_number : '' })}
                  data-testid="edit-client-gst-toggle"
                />
              </div>

              {editFormData.is_gst_registered && (
                <div>
                  <Label htmlFor="edit-gst_number">GST Number <span className="text-red-600">*</span></Label>
                  <Input
                    id="edit-gst_number"
                    data-testid="edit-client-gst-input"
                    placeholder="22AAAAA0000A1Z5"
                    value={editFormData.gst_number}
                    onChange={(e) => setEditFormData({ ...editFormData, gst_number: e.target.value })}
                    required
                  />
                </div>
              )}

              <div>
                <Label htmlFor="edit-branch">Branch <span className="text-red-600">*</span></Label>
                <Input
                  id="edit-branch"
                  data-testid="edit-client-branch-input"
                  placeholder="Mumbai Head Office"
                  value={editFormData.branch}
                  onChange={(e) => setEditFormData({ ...editFormData, branch: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="edit-client_type">Client Type <span className="text-red-600">*</span></Label>
                <Select
                  value={editFormData.client_type}
                  onValueChange={(value) => setEditFormData({ ...editFormData, client_type: value })}
                >
                  <SelectTrigger data-testid="edit-client-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="retainer">
                      <div className="flex items-center gap-2">
                        <RefreshCw className="h-3 w-3" />
                        Retainer
                      </div>
                    </SelectItem>
                    <SelectItem value="one_time">
                      <div className="flex items-center gap-2">
                        <Briefcase className="h-3 w-3" />
                        One-Time Service
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Services as per Agreement</Label>
                <div className="mt-2 grid grid-cols-2 gap-2 max-h-36 overflow-y-auto p-2 border rounded-md bg-slate-50">
                  {services.length === 0 ? (
                    <p className="text-xs text-slate-500 col-span-2">No services available.</p>
                  ) : (
                    services.map(service => (
                      <label key={service.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-white p-1 rounded">
                        <Checkbox
                          checked={editFormData.services_provided.includes(service.name)}
                          onCheckedChange={(checked) => {
                            setEditFormData(prev => ({
                              ...prev,
                              services_provided: checked
                                ? [...prev.services_provided, service.name]
                                : prev.services_provided.filter(s => s !== service.name)
                            }));
                          }}
                          data-testid={`edit-service-checkbox-${service.id}`}
                        />
                        <span>{service.name}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-gradient-to-r from-[#5B6FD6] to-[#10B981]" 
                  data-testid="edit-client-submit-button"
                  disabled={!editFormData.company.trim() || !editFormData.branch.trim() || (editFormData.is_gst_registered && !editFormData.gst_number.trim())}
                >
                  Update Client
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Client Confirmation Dialog */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent data-testid="delete-client-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Client</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{selectedClient?.company}"? This action cannot be undone and may affect associated tasks.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDeleteClient}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-client"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Timeline Override Dialog */}
        <Dialog open={isTimelineDialogOpen} onOpenChange={setIsTimelineDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="timeline-override-dialog">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold flex items-center gap-2">
                <Calendar className="h-5 w-5 text-amber-600" />
                Timeline Overrides - {timelineClient?.company}
              </DialogTitle>
              <DialogDescription>
                Set custom due dates for this client. These override the master timelines.
              </DialogDescription>
            </DialogHeader>
            {timelineLoading ? (
              <div className="py-8 text-center text-slate-400">Loading activities...</div>
            ) : timelineActivities.length === 0 ? (
              <div className="py-8 text-center text-slate-400">
                <p>No activities found for this client's services.</p>
                <p className="text-xs mt-1">Assign services to this client first, then activities will appear here.</p>
              </div>
            ) : (
              <div className="space-y-1">
                <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 rounded-lg text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  <div className="col-span-4">Activity</div>
                  <div className="col-span-2">Service</div>
                  <div className="col-span-2 text-center">Master Due Day</div>
                  <div className="col-span-2 text-center">Custom Due Day</div>
                  <div className="col-span-2 text-center">Action</div>
                </div>
                {timelineActivities.map(activity => {
                  const override = timelineOverrides[activity.id] || {};
                  return (
                    <div key={activity.id} className="grid grid-cols-12 gap-2 items-center px-3 py-2.5 border-b border-slate-100 hover:bg-slate-50 rounded" data-testid={`timeline-row-${activity.id}`}>
                      <div className="col-span-4">
                        <span className="text-sm font-medium text-slate-900">{activity.name}</span>
                        <div className="flex items-center gap-1 mt-0.5">
                          <Badge variant="outline" className="text-xs">{(activity.recurrence_type || '').replace('_', '-')}</Badge>
                        </div>
                      </div>
                      <div className="col-span-2">
                        <Badge className="bg-[#5B6FD6]/10 text-[#5B6FD6] text-xs">{activity.service_name}</Badge>
                      </div>
                      <div className="col-span-2 text-center text-sm text-slate-500">
                        {activity.default_deadline_day ? `Day ${activity.default_deadline_day}` : '-'}
                      </div>
                      <div className="col-span-2 flex justify-center">
                        <Input
                          type="number" min="1" max="31"
                          placeholder="-"
                          className="w-16 h-8 text-center text-sm"
                          value={override.custom_deadline_day || ''}
                          onChange={(e) => handleOverrideChange(activity.id, 'custom_deadline_day', e.target.value)}
                          data-testid={`override-day-${activity.id}`}
                        />
                      </div>
                      <div className="col-span-2 flex justify-center">
                        <Button size="sm" variant="outline" onClick={() => saveOverride(activity.id)}
                          className="text-xs text-[#10B981] border-[#10B981]/30 hover:bg-[#10B981]/10 h-8"
                          data-testid={`save-override-${activity.id}`}>
                          Save
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Duplicate Finder Dialog */}
        <Dialog open={isDupOpen} onOpenChange={setIsDupOpen}>
          <DialogContent className="max-w-2xl" data-testid="duplicates-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                Duplicate Clients
              </DialogTitle>
              <DialogDescription>
                Clients with the same company name (case-insensitive) or the same GST number. Review and delete the extras.
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-96 overflow-y-auto space-y-4 pr-1">
              {dupLoading ? (
                <p className="text-sm text-slate-500 py-4 text-center">Loading...</p>
              ) : (dupData.duplicate_company_groups.length === 0 && dupData.duplicate_gst_groups.length === 0) ? (
                <div className="text-center py-8">
                  <Users className="h-10 w-10 text-green-400 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-slate-700">No duplicates found!</p>
                  <p className="text-xs text-slate-400 mt-1">Your client data is clean.</p>
                </div>
              ) : (
                <>
                  {dupData.duplicate_company_groups.map((group) => (
                    <div key={`name-${group.company_key}`} className="border border-amber-200 rounded-lg p-3 bg-amber-50/40" data-testid={`dup-name-group-${group.company_key}`}>
                      <p className="text-sm font-semibold text-slate-800 mb-2">
                        Company name repeated <span className="text-amber-700">"{group.clients[0].company}"</span> ({group.count} records)
                      </p>
                      <div className="space-y-1.5">
                        {group.clients.map(c => (
                          <div key={c.id} className="flex items-center justify-between bg-white px-3 py-2 rounded border border-slate-200 text-sm">
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-slate-700 truncate">{c.company}</p>
                              <p className="text-xs text-slate-500">{c.branch} {c.gst_number ? `· GST: ${c.gst_number}` : '· Non-GST'}</p>
                            </div>
                            <Button variant="outline" size="sm"
                              onClick={() => deleteDuplicateClient(c.id)}
                              className="h-7 px-2 text-xs text-red-600 border-red-300 hover:bg-red-50 ml-2"
                              data-testid={`dup-delete-${c.id}`}>
                              <Trash2 className="h-3 w-3 mr-1" />
                              Delete
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  {dupData.duplicate_gst_groups.map((group) => (
                    <div key={`gst-${group.gst_number}`} className="border border-red-200 rounded-lg p-3 bg-red-50/40" data-testid={`dup-gst-group-${group.gst_number}`}>
                      <p className="text-sm font-semibold text-slate-800 mb-2">
                        GST number repeated <span className="text-red-700 font-mono">{group.gst_number}</span> ({group.count} records)
                      </p>
                      <div className="space-y-1.5">
                        {group.clients.map(c => (
                          <div key={c.id} className="flex items-center justify-between bg-white px-3 py-2 rounded border border-slate-200 text-sm">
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-slate-700 truncate">{c.company}</p>
                              <p className="text-xs text-slate-500">{c.branch}</p>
                            </div>
                            <Button variant="outline" size="sm"
                              onClick={() => deleteDuplicateClient(c.id)}
                              className="h-7 px-2 text-xs text-red-600 border-red-300 hover:bg-red-50 ml-2">
                              <Trash2 className="h-3 w-3 mr-1" />
                              Delete
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDupOpen(false)}>Close</Button>
              <Button variant="outline" onClick={fetchDuplicates} disabled={dupLoading}>
                {dupLoading ? 'Refreshing…' : 'Refresh'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


        {/* Per-Service Admin Assignment Dialog */}
        <Dialog open={isServiceAssignOpen} onOpenChange={(open) => { if (!open) closeServiceAssignDialog(); }}>
          <DialogContent className="max-w-2xl" data-testid="service-assign-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-purple-600" />
                Per-Service Team Assignment
              </DialogTitle>
              <DialogDescription>
                Assign different services of <span className="font-semibold text-slate-700">{serviceAssignClient?.company}</span> to different admins/teams.
                Existing tasks stay where they are. Only newly generated tasks follow this mapping.
              </DialogDescription>
            </DialogHeader>

            {(!serviceAssignClient?.services_provided || serviceAssignClient.services_provided.length === 0) ? (
              <div className="py-6 text-center text-sm text-slate-500">
                This client has no services assigned. Please add services to the client first.
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 rounded-lg text-[10px] font-semibold text-slate-600 uppercase tracking-wider">
                  <div className="col-span-5">Service</div>
                  <div className="col-span-7">Assigned Admin / Team</div>
                </div>
                {serviceAssignClient.services_provided.map(svc => (
                  <div key={svc} className="grid grid-cols-12 gap-2 items-center px-3 py-2 border border-slate-100 rounded" data-testid={`service-row-${svc}`}>
                    <div className="col-span-5 flex items-center gap-2">
                      <Briefcase className="h-3.5 w-3.5 text-purple-500" />
                      <span className="text-sm text-slate-700 font-medium">{svc}</span>
                    </div>
                    <div className="col-span-7">
                      <Select
                        value={serviceAdminMap[svc] || 'none'}
                        onValueChange={(v) => setServiceAdminMap(prev => ({ ...prev, [svc]: v === 'none' ? '' : v }))}
                      >
                        <SelectTrigger className="h-9 text-sm" data-testid={`service-admin-select-${svc}`}>
                          <SelectValue placeholder="Use default" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— Use client default —</SelectItem>
                          {admins.map(a => (
                            <SelectItem key={a.id} value={a.id}>{a.full_name}{a.role === 'super_admin' ? ' (Super Admin)' : ''}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
                <div className="text-xs text-slate-500 mt-2 px-1">
                  <strong>Default admin:</strong> {serviceAssignClient?.assigned_admin_id ? getAssignedAdminName(serviceAssignClient.assigned_admin_id) : 'None — use "Assign" first or set per service.'}
                </div>
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={closeServiceAssignDialog}>Cancel</Button>
              <Button
                onClick={saveServiceAssignments}
                disabled={savingServiceMap || !serviceAssignClient?.services_provided?.length}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="save-service-assignments"
              >
                {savingServiceMap ? 'Saving…' : 'Save Assignments'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
    </div>
  );
}
